import { getAccessValue } from "../../utils/hooks/access";

const baseByNamespace = {
	clock: "requests.shift",
	pto: "requests.absence",
	schedule: "requests.schedule",
	availability: "requests.availability",
};

const baseByType = {
	shift: "requests.shift",
	"edit-shift": "requests.shift",
	schedule: "requests.schedule",
	"edit-schedule": "requests.schedule",
	"submit-schedule": "requests.schedule",
	"swap-shifts": "requests.schedule",
	"offer-shifts": "requests.schedule",
	availability: "requests.availability",
	absence: "requests.absence",
	cancel: "requests.absence",
	"meal-waive": "requests.timesheet.mealPremiumWaive",
	"miss-punch": "requests.timesheet.missPunch",
	"edit-timesheet": "requests.timesheet.missPunch",
};

const timesheetFilters = [
	"meal-waive",
	"miss-punch",
];

const namespaceKeyMap = {
	pto: "absence",
	clock: "shift",
};

const resolveBasePath = ({ namespace, type, team = false }) => {
	const typeBase = type ? baseByType[type] : null;
	const base = typeBase || baseByNamespace[namespace];
	if (!base) {
		return null;
	}
	return team ? `${base}.team` : base;
};

export const canRequest = (accessMap, { namespace, type, action, team = false }) => {
	const basePath = resolveBasePath({ namespace, type, team });
	if (!basePath) {
		return false;
	}
	return getAccessValue(accessMap, `${basePath}.${action}`);
};

export const getRequestModes = (accessMap, namespace) => {
	const directKey = namespaceKeyMap[namespace] || namespace;
	if (namespace === "timesheet") {
		const my = timesheetFilters.some((type) =>
			canRequest(accessMap, { namespace, type, action: "canView" }),
		) || timesheetFilters.some((type) => accessMap?.requests?.timesheet?.[type === "meal-waive" ? "mealPremiumWaive" : "missPunch"]?.canView);

		const team = timesheetFilters.some((type) =>
			canRequest(accessMap, { namespace, type, action: "canView", team: true }),
		) || timesheetFilters.some((type) => accessMap?.requests?.timesheet?.[type === "meal-waive" ? "mealPremiumWaive" : "missPunch"]?.team?.canView);

		return { my: !!my, team: !!team };
	}

	const my = canRequest(accessMap, { namespace, action: "canView" }) || accessMap?.requests?.[directKey]?.canView;
	const team = canRequest(accessMap, { namespace, action: "canView", team: true }) || accessMap?.requests?.[directKey]?.team?.canView;
	return { my: !!my, team: !!team };
};

export const resolveRequestType = (accessMap, namespace, desiredType) => {
	const modes = getRequestModes(accessMap, namespace);
	if (desiredType === "my" && modes.my) {
		return "my";
	}
	if (desiredType === "team" && modes.team) {
		return "team";
	}
	if (modes.my) {
		return "my";
	}
	if (modes.team) {
		return "team";
	}
	return null;
};

export const getTimesheetFilters = (accessMap, mode) => {
	const team = mode === "team";
	const meal = canRequest(accessMap, {
		namespace: "timesheet",
		type: "meal-waive",
		action: "canView",
		team,
	});
	const miss = canRequest(accessMap, {
		namespace: "timesheet",
		type: "miss-punch",
		action: "canView",
		team,
	});

	const filters = [];
	if (meal && miss) {
		filters.push("all");
	}
	if (meal) {
		filters.push("meal-waive");
	}
	if (miss) {
		filters.push("miss-punch");
	}

	return filters;
};

export const resolveTimesheetFilter = (accessMap, mode, desiredFilter) => {
	const filters = getTimesheetFilters(accessMap, mode);
	if (filters.includes(desiredFilter)) {
		return desiredFilter;
	}
	return filters[0] || null;
};

export const hasAnyRequestAccess = (accessMap) => {
	const namespaces = ["clock", "pto", "schedule", "availability"];
	const hasStandard = namespaces.some((namespace) => {
		const modes = getRequestModes(accessMap, namespace);
		return modes.my || modes.team;
	});

	const directRequests = accessMap?.requests || {};
	const hasDirect = ["shift", "schedule", "availability", "absence"].some((key) => {
		const entry = directRequests?.[key];
		return entry?.canView || entry?.team?.canView;
	});

	if (hasStandard) {
		return true;
	}

	if (hasDirect) {
		return true;
	}

	const timesheetHasAccess = timesheetFilters.some((type) =>
		canRequest(accessMap, { namespace: "timesheet", type, action: "canView" }) ||
		canRequest(accessMap, { namespace: "timesheet", type, action: "canView", team: true }) ||
		directRequests?.timesheet?.[type === "meal-waive" ? "mealPremiumWaive" : "missPunch"]?.canView ||
		directRequests?.timesheet?.[type === "meal-waive" ? "mealPremiumWaive" : "missPunch"]?.team?.canView
	);

	return timesheetHasAccess;
};

export const getFirstRequestRoute = (accessMap) => {
	const order = ["clock", "pto", "schedule", "availability", "timesheet"];
	for (const namespace of order) {
		const type = resolveRequestType(accessMap, namespace);
		if (!type) {
			continue;
		}

		if (namespace === "timesheet") {
			const filter = resolveTimesheetFilter(accessMap, type, "all");
			if (filter) {
				return `/request/${namespace}?requestType=${type}&type=${filter}`;
			}
			continue;
		}

		return `/request/${namespace}?requestType=${type}`;
	}
	return "/";
};
